import { useMemo, useState } from "react";
import { RowData } from "@tanstack/react-table";
import { CheckIcon, PlusCircleIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@capacit-com/lara-law-shared-ui";
import { DataTableFilterProps } from "./data-table-filter-props";
import {
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
  TooltipContent,
} from "@capacit-com/lara-law-shared-ui";
import {
  FilterOptionLabel,
  MinistryFilterHelp,
} from "./filter-option-alias-display";
import {
  formatSelectedOptionTooltipHtml,
  MINISTRY_FILTER_POPOVER_CLASS,
} from "./filter-option-alias-utils";
import {
  filterOptionMatchesQuery,
  MINISTRY_FILTER_KEY,
} from "@/pages/legislation/utils/ministryFilterOptions";

export function DataTableFacetedFilter<TData extends RowData>({
  column,
  title,
  options,
  filterKey,
  alwaysShowFilters = false,
  mode = "multi",
  minSelected = 0,
  isAnyPopoverOpen,
  setIsAnyPopoverOpen,
}: DataTableFilterProps<TData> & {
  alwaysShowFilters?: boolean;
  mode?: "single" | "multi";
  minSelected?: number;
  isAnyPopoverOpen: boolean;
  setIsAnyPopoverOpen: (open: boolean) => void;
}) {
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const facets = column?.getFacetedUniqueValues();
  const rawValue = column?.getFilterValue();
  const selectedSet = new Set(
    Array.isArray(rawValue)
      ? (rawValue as string[])
      : rawValue
        ? [rawValue as string]
        : []
  );
  const selectedSingle = !Array.isArray(rawValue)
    ? (rawValue as string | undefined)
    : undefined;
  const isSingleMode = mode === "single";

  const currentCount = isSingleMode
    ? selectedSingle
      ? 1
      : 0
    : selectedSet.size;
  const cannotClear = currentCount <= (minSelected ?? 0);

  const visibleOptions = useMemo(() => {
    if (!options) return [];
    if (!searchQuery.trim()) return options;
    return options.filter(
      (option) => filterOptionMatchesQuery(option, searchQuery).matches
    );
  }, [options, searchQuery]);

  if ((!options || options.length === 0) && !alwaysShowFilters) {
    return null;
  }

  const selectedValues = isSingleMode
    ? selectedSingle
      ? [selectedSingle]
      : []
    : Array.from(selectedSet);

  const selectedSetHtml = selectedValues
    .map((value) => {
      const option = options?.find((opt) => opt.value === value);
      return option ? formatSelectedOptionTooltipHtml(option) : value;
    })
    .join("<br /><br />");

  const showMinistryHelp = filterKey === MINISTRY_FILTER_KEY;

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setSearchQuery("");
        }
        if (setIsAnyPopoverOpen !== undefined) {
          setIsAnyPopoverOpen(open);
        }
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 border-dashed text-xs px-1.5!"
                onMouseEnter={() => setIsHoveringButton(true)}
                onMouseLeave={() => setIsHoveringButton(false)}
              >
                <PlusCircleIcon />
                {title}
                {(isSingleMode ? !!selectedSingle : selectedSet.size > 0) && (
                  <>
                    <div className="mx-1 h-6 w-px bg-muted dark:bg-muted-foreground" />
                    <Badge
                      variant="secondary"
                      className="rounded-sm px-1 font-normal lg:hidden"
                    >
                      {isSingleMode ? 1 : selectedSet.size}
                      {!isAnyPopoverOpen && isHoveringButton && (
                        <TooltipContent side="bottom">
                          {isSingleMode && selectedSingle ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formatSelectedOptionTooltipHtml(
                                  options?.find(
                                    (opt) => opt.value === selectedSingle
                                  ) ?? {
                                    label: selectedSingle,
                                    value: selectedSingle,
                                  }
                                ),
                              }}
                            />
                          ) : (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: selectedSetHtml,
                              }}
                            />
                          )}
                        </TooltipContent>
                      )}
                    </Badge>
                    <div className="hidden space-x-1 lg:flex">
                      {!isSingleMode && selectedSet.size > 1 ? (
                        <Badge
                          variant="secondary"
                          className="rounded-sm px-1 font-normal"
                        >
                          {selectedSet.size + " valgte"}
                        </Badge>
                      ) : (
                        options
                          ?.filter((option) =>
                            isSingleMode
                              ? selectedSingle === option.value
                              : selectedSet.has(option.value)
                          )
                          .map((option) => (
                            <Badge
                              variant="secondary"
                              key={option.value}
                              className="rounded-sm px-1 font-normal"
                            >
                              {option.label}
                            </Badge>
                          ))
                      )}
                    </div>
                  </>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          {!isSingleMode &&
            selectedSet.size > 1 &&
            !isAnyPopoverOpen &&
            isHoveringButton && (
              <TooltipContent side="bottom">
                <div dangerouslySetInnerHTML={{ __html: selectedSetHtml }} />
              </TooltipContent>
            )}
          {isSingleMode &&
            selectedSingle &&
            !isAnyPopoverOpen &&
            isHoveringButton && (
              <TooltipContent side="bottom">
                <div
                  dangerouslySetInnerHTML={{
                    __html: formatSelectedOptionTooltipHtml(
                      options?.find((opt) => opt.value === selectedSingle) ?? {
                        label: selectedSingle,
                        value: selectedSingle,
                      }
                    ),
                  }}
                />
              </TooltipContent>
            )}
        </Tooltip>
      </TooltipProvider>
      {/* Beside the trigger, not inside it: a <button> cannot contain a <button>. */}
      {showMinistryHelp && <MinistryFilterHelp className="shrink-0" />}
      <PopoverContent
        className={showMinistryHelp ? MINISTRY_FILTER_POPOVER_CLASS : "p-0"}
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={title}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>Ingen resultater.</CommandEmpty>
            <CommandGroup>
              {visibleOptions.map((option) => {
                const isSelected = isSingleMode
                  ? selectedSingle === option.value
                  : selectedSet.has(option.value);
                const { matchedViaAlias } = filterOptionMatchesQuery(
                  option,
                  searchQuery
                );
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    className="items-start gap-2 py-2"
                    onSelect={() => {
                      if (isSingleMode) {
                        if (selectedSingle === option.value) {
                          if (!cannotClear) {
                            column?.setFilterValue(undefined);
                          }
                        } else {
                          column?.setFilterValue(option.value);
                        }
                        return;
                      }

                      const isAlready = selectedSet.has(option.value);
                      if (isAlready) {
                        if (currentCount - 1 < (minSelected ?? 0)) {
                          return;
                        }
                        selectedSet.delete(option.value);
                      } else {
                        selectedSet.add(option.value);
                      }
                      const filterValues = Array.from(selectedSet);
                      column?.setFilterValue(
                        filterValues.length ? filterValues : undefined
                      );
                    }}
                  >
                    <div
                      className={cn(
                        "mt-0.5 mr-2 flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className="size-4 text-primary-foreground" />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 size-4 shrink-0 text-muted-foreground" />
                    )}
                    <FilterOptionLabel
                      option={option}
                      matchedViaAlias={matchedViaAlias}
                    />
                    {facets?.get(option.value) && (
                      <span className="ml-auto flex size-4 shrink-0 items-center justify-center font-mono text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {(isSingleMode ? !!selectedSingle : selectedSet.size > 0) &&
              !cannotClear && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => column?.setFilterValue(undefined)}
                      className="justify-center text-center"
                    >
                      Ryd filter
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
